import { useMemo } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useFieldArray, useFormContext, useWatch } from 'FrontRoyalReactHookForm';
import ChapterRow from './ChapterRow';
import { type EmbeddedLesson, type StreamFormValues } from './StreamEditor.types';

type Props = {
    lessonsById: Record<string, EmbeddedLesson>;

    // Every lesson of this locale an editor could add, already filtered for the assessment rule.
    availableLessons: EmbeddedLesson[];
    lessonsLoaded: boolean;
    assessmentEditingLocked: boolean;
    editorUrlForLesson: (lessonId: string) => string;
};

export default function ChaptersSection({
    lessonsById,
    availableLessons,
    lessonsLoaded,
    assessmentEditingLocked,
    editorUrlForLesson,
}: Props) {
    const { control } = useFormContext<StreamFormValues>();
    const { fields, append, remove, move } = useFieldArray({ control, name: 'chapters' });

    // useWatch, not watch: watch returns the same array after a lesson is added or removed inside
    // a chapter, so the memos below would keep offering a lesson the course already holds.
    const chapters = useWatch({ control, name: 'chapters' });

    // A lesson already in the course is not offered again, which is what the Angular otherLessons
    // list did.
    const lessonIdsInCourse = useMemo(
        () => new Set((chapters || []).flatMap(chapter => (chapter.lesson_hashes || []).map(hash => hash.lesson_id))),
        [chapters],
    );

    const lessonOptions = useMemo(
        () => availableLessons.filter(lesson => !lessonIdsInCourse.has(lesson.id)),
        [availableLessons, lessonIdsInCourse],
    );

    const comingSoonCount = useMemo(
        () =>
            (chapters || []).reduce(
                (count, chapter) => count + (chapter.lesson_hashes || []).filter(hash => hash.coming_soon).length,
                0,
            ),
        [chapters],
    );

    function removeChapter(index: number) {
        const title = chapters?.[index]?.title || '';

        // The same question the Angular chapter asked. A chapter holds lessons, and deleting it
        // takes them out of the course.
        if (window.confirm(`You sure you want to delete "${title}"?`)) remove(index);
    }

    return (
        <Stack spacing={2}>
            {comingSoonCount > 0 && (
                <Typography variant="body2" color="secondary.main" sx={{ alignSelf: 'flex-end' }}>
                    {`Coming Soon: ${comingSoonCount} lesson(s)`}
                </Typography>
            )}

            {assessmentEditingLocked && (
                <Alert severity="info">
                    The ability to add/remove SMARTCASEs has been disabled because assessment editing has been locked.
                </Alert>
            )}

            {fields.map((field, index) => (
                <ChapterRow
                    key={field.id}
                    index={index}
                    chapterCount={fields.length}
                    lessonsById={lessonsById}
                    lessonOptions={lessonOptions}
                    lessonsLoaded={lessonsLoaded}
                    assessmentEditingLocked={assessmentEditingLocked}
                    editorUrlForLesson={editorUrlForLesson}
                    onMoveChapter={move}
                    onRemoveChapter={() => removeChapter(index)}
                />
            ))}

            <Button
                data-id="add_chapter"
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={() => append({ title: '', lesson_ids: [], lesson_hashes: [] })}
                sx={{ alignSelf: 'flex-start' }}
            >
                Add Chapter
            </Button>
        </Stack>
    );
}
